/**
 * M2 Skins — Custom JS
 */

/* ==========================================================================
   M2Gallery — Custom product image gallery
   Replaces the theme's Flickity-based ProductGallery.
   Features: fade transitions, thumbnail strip with dock magnification,
   variant-based filtering, hover zoom, canvas reflection,
   JPG white-background removal, content boundary detection.
   ========================================================================== */

(function() {
  'use strict';

  var PROCESS_SIZE = 800;       // max dimension for offscreen processing canvas
  var WHITE_THRESHOLD = 248;    // RGB channels >= this = "white" (accounts for JPEG artifacts)
  // Gap between the detected content bottom and the start of the reflection.
  var CONTENT_BUFFER = 30;
  // Gap between the reflection's bottom edge and the thumbstrip, so the
  // reflection fills the stage but doesn't touch the thumbnails.
  var END_BUFFER = 12;
  var REFLECTION_OPACITY = 0.72; // up from 0.55 — stronger anchor so the device doesn't float
  // Material-change wipe (see _beginWipe). QA can override any field before this
  // file loads, e.g. window.M2_WIPE = { dur: 2000, freeze: 0.5 }.
  var M2_WIPE = window.M2_WIPE = Object.assign(
    { on: true, dur: 650, feather: 7, angle: 127, glint: 0.22, freeze: null }, window.M2_WIPE || {});

  function M2Gallery(el) {
    this.el = el;
    this.main = el.querySelector('[data-m2-gallery-main]');
    this.thumbTrack = el.querySelector('[data-m2-thumb-track]');
    this.prevBtn = el.querySelector('[data-m2-thumb-prev]');
    this.nextBtn = el.querySelector('[data-m2-thumb-next]');
    this.zoomLens = el.querySelector('[data-m2-zoom-lens]');
    this.zoomWindow = el.querySelector('[data-m2-zoom-window]');
    this.reflectionCanvas = el.querySelector('[data-m2-reflection]');
    this.productOptions = JSON.parse(el.getAttribute('data-product-options') || '[]');

    this.slides = Array.from(this.main.querySelectorAll('.m2-gallery__slide'));
    this.thumbs = Array.from(this.thumbTrack.querySelectorAll('.m2-gallery__thumb'));
    this.activeSlide = null;
    this.activeThumb = null;
    this.zoomImg = null;
    this.isZooming = false;
    this._processedCache = {};

    this._init();
  }

  M2Gallery.prototype._init = function() {
    this._bindThumbs();
    this._bindArrows();
    this._bindVariantChange();
    this._bindZoom();
    this._bindThumbDrag();
    this._bindScrollEdges();
    this._bindTransparencyDetection();
    this._bindResize();
    this._bindSwatchChange();

    // Initial slug filter determines which slides are visible and activates the first one
    this._filterBySlug();
    this._updateArrows();
  };

  /* =========================================================================
     Core: activate a slide by media ID
     ========================================================================= */
  M2Gallery.prototype._activate = function(mediaId) {
    var self = this;
    mediaId = String(mediaId);

    // Any slide change that isn't the wipe's own (_beginWipe activates before it
    // registers the wipe) tears a running wipe down so nothing gets stuck on top.
    this._cancelWipe();

    // Fade the old reflection out as soon as the slide change starts. When the
    // new reflection is drawn (after image load), it'll fade back in via its
    // own CSS transition — in sync with the image's fade-in (both use opacity
    // 0.4s ease). Without this, the reflection stayed fully visible during the
    // transition and only swapped content once the new image finished loading.
    // Skip when the user is re-activating the same slide (no real change).
    if (this.reflectionCanvas && this.activeSlide &&
        this.activeSlide.getAttribute('data-media-id') !== mediaId) {
      this.reflectionCanvas.style.opacity = '0';
    }

    this.slides.forEach(function(s) {
      s.classList.toggle('is-active', s.getAttribute('data-media-id') === mediaId);
    });
    this.thumbs.forEach(function(t) {
      t.classList.toggle('is-active', t.getAttribute('data-media-id') === mediaId);
    });

    this.activeSlide = this.main.querySelector('.m2-gallery__slide.is-active');
    this.activeThumb = this.thumbTrack.querySelector('.m2-gallery__thumb.is-active');

    if (this.activeThumb && this.thumbTrack) {
      // Horizontal-only scroll within the thumb track — don't bubble to the page.
      // scrollIntoView with block:'nearest' can still shift vertical scroll when
      // the thumbstrip is below the fold, which is unwanted.
      var track = this.thumbTrack;
      var thumb = this.activeThumb;
      var target = thumb.offsetLeft - (track.clientWidth / 2) + (thumb.offsetWidth / 2);
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    this._updateArrows();
    this._processAndDisplay();
  };

  M2Gallery.prototype._firstVisible = function() {
    for (var i = 0; i < this.slides.length; i++) {
      if (!this.slides[i].classList.contains('is-filtered')) return this.slides[i];
    }
    return this.slides[0];
  };

  /* =========================================================================
     Image processing: white removal (JPG) + content boundary detection
     ========================================================================= */

  /**
   * Where the image actually renders within the main container.
   *
   * Previously this assumed `object-fit: contain` and recomputed bounds from
   * natural image size + container size. That broke whenever the layout used
   * a different fit — e.g. the wizard gallery uses `width: 100%; height: auto`
   * with a `margin-top: -15%` to crop the top of product images (there are
   * 8700 images, can't re-crop them server-side).
   *
   * Using getBoundingClientRect instead returns the actually-rendered box of
   * the image element, which is correct for any fit mode AND any margin/transform
   * shift. Works transparently with contain-fit layouts too (gives identical
   * results to the old math for those).
   */
  M2Gallery.prototype._getImageBounds = function(img) {
    var cW = this.main.clientWidth;
    var cH = this.main.clientHeight;
    var nW = img.naturalWidth;
    var nH = img.naturalHeight;
    if (!nW || !nH) return { x: 0, y: 0, width: cW, height: cH };

    var mainRect = this.main.getBoundingClientRect();
    var imgRect  = img.getBoundingClientRect();

    // Rendered dimensions match the rect; but clamp bounds.y to 0 and cap
    // bounds.height at cH so the reflection positions at the VISIBLE bottom of
    // the image (not below clipped overflow). For margin-top cropped layouts,
    // the image's layout top is negative; we want reflection right below the
    // container bottom = visible bottom.
    var renderedW = imgRect.width;
    var renderedH = imgRect.height;
    var boxX = imgRect.left - mainRect.left;
    var boxY = imgRect.top  - mainRect.top;
    var scale = renderedW / nW;

    // The image's effective VISIBLE region inside the container:
    // - If layout top is above container top (margin-top crop), visible top = 0.
    // - Visible bottom is min(layout bottom, container bottom).
    var visibleTop    = Math.max(0, boxY);
    var visibleBottom = Math.min(cH, boxY + renderedH);
    var visibleHeight = Math.max(0, visibleBottom - visibleTop);

    return {
      x: boxX,
      y: visibleTop,
      width: renderedW,
      height: visibleHeight,
      scale: scale,
      /* Raw layout top + full rendered height, unclamped — so callers can
         project a source-image ratio onto the rendered image accurately
         even when the image is cropped by a negative margin. */
      layoutY: boxY,
      renderedHeight: renderedH,
      /* Fraction of the source image that's HIDDEN above the visible area —
         lets the reflection draw from the correct source strip instead of
         pulling from the top (cropped) edge. */
      sourceTopSkipRatio: (boxY < 0) ? (-boxY / renderedH) : 0
    };
  };

  /**
   * After activating a slide, process its image:
   * - JPGs: flood-fill remove contiguous white from edges, then detect content bottom
   * - PNG/WebP: detect content bottom via alpha channel
   * - Other/video: basic reflection fallback
   */
  M2Gallery.prototype._processAndDisplay = function() {
    var self = this;
    var slide = this.activeSlide;
    if (!slide) { this._hideReflection(); return; }

    var img = slide.querySelector('.m2-gallery__image');
    if (!img) { this._hideReflection(); return; }

    // Keep image hidden until reflection (if any) is ready, so they fade in together
    img.classList.remove('is-loaded');
    // Force a reflow so the opacity:0 start-state is committed before
    // any subsequent opacity change can trigger the transition.
    // Without this, display:none→block + immediate is-loaded can skip the fade.
    /* eslint-disable-next-line no-unused-expressions */
    img.offsetWidth;

    function reveal() {
      // Fire in the same sync call as _drawReflection's canvas.style.opacity
      // so both transitions start on the same frame (no rAF defer)
      img.classList.add('is-loaded');
    }

    function run() {
      self._processImage(img, function(result) {
        if (!result) { self._hideReflection(); reveal(); return; }

        // Replace displayed image with white-removed version
        if (result.blobUrl && !img._m2processed) {
          img._m2originalSrc = img.currentSrc || img.src;
          img.removeAttribute('srcset'); // prevent srcset from overriding our blob
          img.src = result.blobUrl;
          img._m2processed = true;
        }

        // Mark gallery format for CSS bg rules — detect from URL, not processing
        var imgSrc = (img._m2originalSrc || img.currentSrc || img.src || '').toLowerCase();
        var isTransparent = /\.png|\.webp/.test(imgSrc);
        self.el.classList.toggle('has-png-images', isTransparent);
        self.el.classList.toggle('is-jpg', !isTransparent);

        // Draw reflection only for transparent formats (PNG/WebP), skip JPGs
        if (isTransparent) {
          self._lastProcessResult = result;
          // If we swapped src to a blob, wait for the new src to decode
          // before drawing reflection + fading in — ensures no flash
          if (img.src && img.src.indexOf('blob:') === 0 && !img.complete) {
            img.addEventListener('load', function onLoad() {
              img.removeEventListener('load', onLoad);
              self._drawReflection(result);
              reveal();
            });
          } else {
            self._drawReflection(result);
            reveal();
          }
        } else {
          self._hideReflection();
          self._lastProcessResult = null;
          reveal();
        }
      });
    }

    if (img.complete && img.naturalWidth) {
      run();
    } else {
      img.addEventListener('load', run, { once: true });
    }
  };

  /**
   * Process an image: flood-fill white removal (JPG) + content boundary scan.
   * Results are cached per src URL.
   */
  M2Gallery.prototype._processImage = function(img, callback) {
    var self = this;
    // Use the actual loaded URL (currentSrc respects srcset selection)
    var originalSrc = img._m2originalSrc || img.currentSrc || img.src || '';
    var cacheKey = originalSrc;

    // Return cached result
    if (self._processedCache[cacheKey]) {
      callback(self._processedCache[cacheKey]);
      return;
    }

    // Scale down for processing performance
    var maxDim = Math.max(img.naturalWidth, img.naturalHeight);
    var scale = Math.min(1, PROCESS_SIZE / maxDim);
    var w = Math.round(img.naturalWidth * scale);
    var h = Math.round(img.naturalHeight * scale);
    if (w < 1 || h < 1) { callback(null); return; }

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');

    // Load a CORS-enabled copy for pixel access
    // Add cache-buster to avoid CORS conflict with non-CORS cached copies
    var crossImg = new Image();
    crossImg.crossOrigin = 'anonymous';
    var corsUrl = originalSrc + (originalSrc.indexOf('?') > -1 ? '&' : '?') + '_m2cors=1';

    crossImg.onload = function() {
      ctx.drawImage(crossImg, 0, 0, w, h);

      var imageData, data;
      try {
        imageData = ctx.getImageData(0, 0, w, h);
        data = imageData.data;
      } catch (e) {
        // Canvas tainted (CORS issue) — fall back gracefully
        console.warn('[M2Gallery] CORS blocked pixel access for:', originalSrc);
        callback({ contentBottomRatio: 1, canvas: null });
        return;
      }

      // --- Flood-fill background color from edges → transparent ---
      // Previously this only matched near-WHITE pixels, so images with black,
      // grey, or any other non-white background didn't get their padding
      // detected — meaning contentBottomRatio stayed at 1 and the reflection
      // anchored below the device's actual edge.
      //
      // Now we auto-detect the edge color by sampling corners + mid-edge pixels
      // and flood-fill everything similar within a tolerance. Handles white,
      // black, grey, and uniform colored backgrounds generically.
      var isTransparentFormat = /\.png|\.webp/i.test(originalSrc);
      var didRemoveWhite = false;
      var EDGE_COLOR_TOLERANCE = 22; // per-channel difference cutoff

      // Sample edges to detect whether the background is opaque solid color.
      // For already-transparent-background images (all of ours), most edge
      // samples will have alpha=0 and we should SKIP flood-fill entirely —
      // running it can incorrectly consume white/light surfaces on the device
      // itself. Flood-fill only kicks in when >= 75% of edge samples are opaque
      // AND agree in color (indicating a genuine solid background to remove).
      function samplePxForBg(sx, sy) {
        var ip = (sy * w + sx) * 4;
        if (data[ip + 3] < 200) return null;   // require near-opaque edge pixel
        return [data[ip], data[ip + 1], data[ip + 2]];
      }
      var samplePoints = [
        [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1],
        [w >> 1, 0], [0, h >> 1], [w - 1, h >> 1], [w >> 1, h - 1]
      ];
      var edgeR = 0, edgeG = 0, edgeB = 0, edgeCount = 0;
      for (var sp = 0; sp < samplePoints.length; sp++) {
        var s = samplePxForBg(samplePoints[sp][0], samplePoints[sp][1]);
        if (s) { edgeR += s[0]; edgeG += s[1]; edgeB += s[2]; edgeCount++; }
      }
      var edgeHasSolidBg = edgeCount >= 6;   // 6 of 8 sample points opaque
      var bgR = edgeCount ? edgeR / edgeCount : 255;
      var bgG = edgeCount ? edgeG / edgeCount : 255;
      var bgB = edgeCount ? edgeB / edgeCount : 255;

      if (isTransparentFormat && edgeHasSolidBg) {
        var visited = new Uint8Array(w * h);
        var queue = [];

        var x, y;
        for (x = 0; x < w; x++) {
          queue.push(x);
          queue.push((h - 1) * w + x);
        }
        for (y = 1; y < h - 1; y++) {
          queue.push(y * w);
          queue.push(y * w + w - 1);
        }

        var head = 0;
        var removedCount = 0;
        while (head < queue.length) {
          var idx = queue[head++];
          if (visited[idx]) continue;

          var px = idx * 4;
          var dR = Math.abs(data[px]     - bgR);
          var dG = Math.abs(data[px + 1] - bgG);
          var dB = Math.abs(data[px + 2] - bgB);
          if (data[px + 3] > 0 &&
              dR < EDGE_COLOR_TOLERANCE &&
              dG < EDGE_COLOR_TOLERANCE &&
              dB < EDGE_COLOR_TOLERANCE) {
            visited[idx] = 1;
            data[px + 3] = 0;
            removedCount++;

            var cx = idx % w;
            var cy = (idx - cx) / w;
            if (cx > 0 && !visited[idx - 1]) queue.push(idx - 1);
            if (cx < w - 1 && !visited[idx + 1]) queue.push(idx + 1);
            if (cy > 0 && !visited[idx - w]) queue.push(idx - w);
            if (cy < h - 1 && !visited[idx + w]) queue.push(idx + w);
          }
        }

        didRemoveWhite = removedCount > 0;
        if (didRemoveWhite) {
          ctx.putImageData(imageData, 0, 0);
          imageData = ctx.getImageData(0, 0, w, h);
          data = imageData.data;
        }
      }

      // --- Find content bottom boundary ---
      // Scan alpha channel from bottom up. A row counts as "content" when we
      // find a RUN of MIN_ADJACENT or more contiguous pixels whose alpha is
      // above ALPHA_THRESHOLD. We only count SOLID body pixels (alpha > 200)
      // and deliberately skip semi-transparent content like drop-shadows and
      // WebP encoder fringe — those were firing the detector 10-20% of the
      // image height below the real device edge, producing a visible gap
      // between the device and the reflection. Anti-aliasing at the true
      // device edge is only 1-2px thick, so undershoot is negligible and
      // CONTENT_BUFFER absorbs it.
      var contentBottom = -1;
      // Low adjacency so we still detect thin solid tips on angled views
      // (a corner peak might only be a handful of pixels wide). The high
      // alpha gate alone does the filtering — shadows never reach alpha 200.
      var MIN_ADJACENT = 4;
      var ALPHA_THRESHOLD = 200;

      if (isTransparentFormat) {
        outer: for (var row = h - 1; row >= 0; row--) {
          var run = 0;
          for (var col = 0; col < w; col++) {
            if (data[(row * w + col) * 4 + 3] > ALPHA_THRESHOLD) {
              run++;
              if (run >= MIN_ADJACENT) {
                contentBottom = row;
                break outer;
              }
            } else {
              run = 0;
            }
          }
        }
        if (contentBottom < 0) contentBottom = h - 1; // nothing detected — fall back
      } else {
        // JPG: no reliable boundary detection (shadows/reflections cause misfires)
        contentBottom = h - 1;
      }

      var result = {
        contentBottomRatio: (contentBottom + 1) / h,
        canvas: canvas,
        width: w,
        height: h,
        blobUrl: null,
        isJPG: !isTransparentFormat
      };

      // If white was removed, create blob URL for display (PNG preserves transparency)
      if (didRemoveWhite) {
        canvas.toBlob(function(blob) {
          if (blob) {
            result.blobUrl = URL.createObjectURL(blob);
          }
          self._processedCache[cacheKey] = result;
          callback(result);
        }, 'image/png');
      } else {
        self._processedCache[cacheKey] = result;
        callback(result);
      }
    };

    crossImg.onerror = function() {
      // CORS or network failure — skip processing
      console.warn('[M2Gallery] Failed to load image for processing:', originalSrc);
      callback({ contentBottomRatio: 1, canvas: null });
    };

    crossImg.src = corsUrl;
  };

  /* =========================================================================
     Reflection: draw at content boundary
     ========================================================================= */
  M2Gallery.prototype._drawReflection = function(processResult) {
    var canvas = this.reflectionCanvas;
    if (!canvas || !this.activeSlide) return;

    var img = this.activeSlide.querySelector('.m2-gallery__image');
    if (!img || !img.naturalWidth) { this._hideReflection(); return; }

    var srcCanvas = processResult ? processResult.canvas : null;
    var contentBottomRatio = processResult ? processResult.contentBottomRatio : 1;

    var bounds = this._getImageBounds(img);
    var dpr = window.devicePixelRatio || 1;
    var mainW = this.main.clientWidth;

    // Source to reflect from
    var drawSource = srcCanvas || img;
    var srcW = srcCanvas ? srcCanvas.width : img.naturalWidth;
    var srcH = srcCanvas ? srcCanvas.height : img.naturalHeight;

    // Use the image's actual rendered scale (bounds.scale = renderedW / naturalW)
    // so the reflection strip's source→display compression matches the visible
    // image at 1:1. Previously used bounds.height/srcH which gave the contain-fit
    // scale and could mismatch when the layout is width-driven (wizard gallery).
    var displayScale = bounds.scale || (bounds.height / (srcH * contentBottomRatio || srcH));

    // Position reflection anchor first — right under the detected content bottom
    // in the rendered image. Use layoutY + renderedHeight (unclamped) so a
    // ratio within the full source image projects accurately onto the rendered
    // layout even when it's cropped by a negative margin.
    var mainRect = this.main.getBoundingClientRect();
    var galleryRect = this.el.getBoundingClientRect();
    var reflectionLeft = mainRect.left - galleryRect.left;
    var mainTopInGallery = mainRect.top - galleryRect.top;
    var contentBottomY = bounds.layoutY + (contentBottomRatio * bounds.renderedHeight);
    // Clamp so the reflection never starts above the visible image area (can
    // happen if content is detected inside the cropped-out top strip).
    if (contentBottomY < bounds.y) contentBottomY = bounds.y;
    var reflectionTop = mainTopInGallery + contentBottomY + CONTENT_BUFFER;

    // Dynamic height: fill from the anchor down to just above the bottom edge of
    // the surrounding card (passes behind the thumbstrip). The available space
    // varies image-to-image because the content bottom moves with each device,
    // so we always stretch the reflection to the card floor rather than using a
    // fixed height.
    var bottomAnchorEl = this._cardEl || (this._cardEl = this.el.closest('.card') || this.main);
    var bottomAnchorRect = bottomAnchorEl.getBoundingClientRect();
    var bottomAnchorInGallery = bottomAnchorRect.bottom - galleryRect.top;
    var availableDisplayH = Math.max(0, bottomAnchorInGallery - reflectionTop - END_BUFFER);

    // Source strip (in image pixels) = available display height scaled back up,
    // clamped so we never try to mirror more of the image than actually exists
    // above the content bottom.
    var srcContentBottom = Math.round(contentBottomRatio * srcH);
    var srcStripH = Math.round(availableDisplayH / displayScale);
    srcStripH = Math.min(srcStripH, srcContentBottom);
    var srcStripY = srcContentBottom - srcStripH;
    if (srcStripH < 1) { this._hideReflection(); return; }

    var actualDisplayH = Math.round(srcStripH * displayScale);
    if (actualDisplayH > availableDisplayH) actualDisplayH = availableDisplayH;
    canvas.style.top = reflectionTop + 'px';
    canvas.style.left = reflectionLeft + 'px';
    canvas.style.visibility = 'visible';

    canvas.width = mainW * dpr;
    canvas.height = actualDisplayH * dpr;
    canvas.style.width = mainW + 'px';
    canvas.style.height = actualDisplayH + 'px';

    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw flipped: source bottom row → canvas top row (1:1 scale)
    ctx.save();
    ctx.scale(1, -1);
    ctx.translate(0, -canvas.height);

    ctx.drawImage(
      drawSource,
      0, srcStripY, srcW, srcStripH,
      bounds.x * dpr, 0, bounds.width * dpr, canvas.height
    );

    ctx.restore();

    // Fade in (CSS transition handles the animation)
    canvas.style.opacity = String(REFLECTION_OPACITY);
  };

  M2Gallery.prototype._hideReflection = function() {
    if (this.reflectionCanvas) {
      this.reflectionCanvas.style.opacity = '0';
    }
  };

  /* =========================================================================
     Resize handler — redraw reflection on window resize / orientation change
     ========================================================================= */
  M2Gallery.prototype._bindResize = function() {
    var self = this;
    var resizeTimer = null;

    function onResize() {
      // Hide immediately so the stale reflection doesn't linger during resize
      self._hideReflection();
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        if (self._lastProcessResult) {
          self._drawReflection(self._lastProcessResult);
        }
      }, 150);
    }

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', function() {
      self._hideReflection();
      setTimeout(onResize, 300);
    });
  };

  /* =========================================================================
     Transparency detection (PNG / WebP / processed JPG)
     ========================================================================= */
  M2Gallery.prototype._bindTransparencyDetection = function() {
    var self = this;
    var observer = new MutationObserver(function() {
      setTimeout(function() {
        if (!self.activeSlide) return;
        var img = self.activeSlide.querySelector('.m2-gallery__image');
        if (!img) return;
        var src = (img._m2originalSrc || img.currentSrc || img.src || '').toLowerCase();
        var isTransparentFormat = /\.png|\.webp/.test(src);
        var isProcessedJPG = !!img._m2processed;
        self.el.classList.toggle('has-png-images', isTransparentFormat || isProcessedJPG);
      }, 50);
    });
    observer.observe(this.main, { subtree: true, attributes: true, attributeFilter: ['class'] });
  };

  /* =========================================================================
     Thumbnail clicks
     ========================================================================= */
  M2Gallery.prototype._bindThumbs = function() {
    var self = this;
    this.thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        self._activate(thumb.getAttribute('data-media-id'));
      });
    });
  };

  /* =========================================================================
     Arrow navigation
     ========================================================================= */
  M2Gallery.prototype._bindArrows = function() {
    var self = this;
    this.prevBtn.addEventListener('click', function() { self._stepThumb(-1); });
    this.nextBtn.addEventListener('click', function() { self._stepThumb(1); });
  };

  M2Gallery.prototype._stepThumb = function(dir) {
    var visible = this._visibleThumbs();
    if (!visible.length) return;

    var currentIdx = -1;
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].classList.contains('is-active')) { currentIdx = i; break; }
    }

    var nextIdx = currentIdx + dir;
    if (nextIdx < 0) nextIdx = 0;
    if (nextIdx >= visible.length) nextIdx = visible.length - 1;

    this._activate(visible[nextIdx].getAttribute('data-media-id'));
  };

  M2Gallery.prototype._visibleThumbs = function() {
    return this.thumbs.filter(function(t) {
      return !t.classList.contains('is-filtered');
    });
  };

  M2Gallery.prototype._updateArrows = function() {
    var visible = this._visibleThumbs();
    var currentIdx = -1;
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].classList.contains('is-active')) { currentIdx = i; break; }
    }
    this.prevBtn.disabled = (currentIdx <= 0);
    this.nextBtn.disabled = (currentIdx >= visible.length - 1);
  };

  /* =========================================================================
     Scroll-edge detection: hide fade + arrow at each end
     ========================================================================= */
  M2Gallery.prototype._bindScrollEdges = function() {
    var self = this;
    var track = this.thumbTrack;

    function update() {
      var scrollLeft = track.scrollLeft;
      var maxScroll = track.scrollWidth - track.clientWidth;
      /* When the track has no meaningful overflow (e.g. 3 thumbs that
         all fit inside the viewport), hide both arrows entirely
         instead of letting the right arrow show because of a few
         pixels of padding/rounding overflow. */
      var noOverflow = maxScroll <= 8;
      var atStart = noOverflow || scrollLeft <= 2;
      var atEnd   = noOverflow || scrollLeft >= maxScroll - 2;

      self.prevBtn.classList.toggle('is-hidden', atStart);
      self.nextBtn.classList.toggle('is-hidden', atEnd);
      track.classList.toggle('at-start', atStart);
      track.classList.toggle('at-end', atEnd);
    }

    track.addEventListener('scroll', update, { passive: true });
    // Also update after layout settles and on resize
    setTimeout(update, 100);
    window.addEventListener('resize', update);
  };

  /* =========================================================================
     Mouse drag + touch drag on thumbnail strip (with momentum)
     ========================================================================= */
  M2Gallery.prototype._bindThumbDrag = function() {
    var track = this.thumbTrack;
    var isDragging = false;
    var startX = 0;
    var scrollStart = 0;
    var hasMoved = false;
    var velocity = 0;
    var lastX = 0;
    var lastTime = 0;
    var momentumId = null;

    function stopMomentum() {
      if (momentumId) { cancelAnimationFrame(momentumId); momentumId = null; }
    }

    function onDown(e) {
      stopMomentum();
      isDragging = true;
      hasMoved = false;
      velocity = 0;
      var x = (e.touches ? e.touches[0].clientX : e.clientX);
      startX = x;
      lastX = x;
      lastTime = Date.now();
      scrollStart = track.scrollLeft;
      track.style.scrollBehavior = 'auto';
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
      if (!e.touches) e.preventDefault();
    }

    function onMove(e) {
      if (!isDragging) return;
      var x = (e.touches ? e.touches[0].clientX : e.clientX);
      var dx = startX - x;
      if (Math.abs(dx) > 3) hasMoved = true;

      // Track velocity for momentum
      var now = Date.now();
      var dt = now - lastTime;
      if (dt > 0) {
        velocity = (lastX - x) / dt; // px per ms
      }
      lastX = x;
      lastTime = now;

      track.scrollLeft = scrollStart + dx;
    }

    function onUp() {
      if (!isDragging) return;
      isDragging = false;
      track.style.cursor = '';
      track.style.userSelect = '';

      // Apply momentum
      var v = velocity * 16; // convert to px per frame (~16ms)
      var friction = 0.95;

      function tick() {
        if (Math.abs(v) < 0.5) {
          track.style.scrollBehavior = '';
          return;
        }
        track.scrollLeft += v;
        v *= friction;
        momentumId = requestAnimationFrame(tick);
      }

      if (Math.abs(v) > 1) {
        momentumId = requestAnimationFrame(tick);
      } else {
        track.style.scrollBehavior = '';
      }
    }

    // Mouse events
    track.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    // Touch events — passive: false on touchmove so we can prevent vertical scroll jank
    track.addEventListener('touchstart', onDown, { passive: true });
    track.addEventListener('touchmove', function(e) {
      if (isDragging && hasMoved) e.preventDefault();
      onMove(e);
    }, { passive: false });
    track.addEventListener('touchend', onUp);

    // Prevent click on thumbnails after a drag
    track.addEventListener('click', function(e) {
      if (hasMoved) {
        e.stopPropagation();
        e.preventDefault();
      }
    }, true);
  };

  /* =========================================================================
     Variant change: filter slides & thumbnails
     ========================================================================= */
  M2Gallery.prototype._bindVariantChange = function() {
    var self = this;

    var section = this.el.closest('[data-section-type="product"]');
    if (section) {
      section.addEventListener('variant:change', function(e) {
        self._onVariantChange(e.detail.variant);
      });
    }

    document.addEventListener('variant:changed', function(e) {
      self._onVariantChange(e.detail.variant);
    });
  };

  M2Gallery.prototype._onVariantChange = function(variant) {
    if (!variant) return;

    // Store variant options for slug filtering
    this._lastVariant = variant;

    // Run slug-based filtering
    this._filterBySlug();

    // If active slide got filtered, jump to first visible or featured media
    if (this.activeSlide && this.activeSlide.classList.contains('is-filtered')) {
      if (variant.featured_media) {
        this._activate(variant.featured_media.id);
      } else {
        var first = this._firstVisible();
        if (first) this._activate(first.getAttribute('data-media-id'));
      }
    } else if (variant.featured_media) {
      this._activate(variant.featured_media.id);
    }
  };

  /* =========================================================================
     Slug-based image filtering + sorting
     ========================================================================= */

  /**
   * Map a kit option value to its filename slug.
   * "Essentials" → "-ess-", "Premium" → "-prm-", "Ultimate" → "-ult-".
   * (Legacy "Pro" → "-pro-" kept for older products that used a Pro kit.)
   */
  M2Gallery.prototype._getKitSlug = function(kitValue) {
    if (!kitValue) return null;
    var k = kitValue.toLowerCase();
    if (k.indexOf('essential') >= 0) return '-ess-';
    if (k.indexOf('premium') >= 0)   return '-prm-';
    if (k.indexOf('ultimate') >= 0)  return '-ult-';
    if (k.indexOf('pro') >= 0)       return '-pro-';
    return null;
  };

  /**
   * Get the currently selected material/vinyl slug from the swatch UI.
   * Tries multiple approaches since swatches render differently based on product_url.
   */
  M2Gallery.prototype._getMaterialSlug = function() {
    // Approach 1: checked radio with data-material-slug
    var radio = document.querySelector('.pf-swatch-wrap .color-swatch__radio:checked[data-material-slug]');
    if (radio) return radio.getAttribute('data-material-slug').toLowerCase();

    // Approach 2: selected swatch link (product_url swatches use <a> with is-selected)
    var selectedLink = document.querySelector('.pf-swatch-wrap .color-swatch__item.is-selected');
    if (selectedLink) {
      var title = selectedLink.getAttribute('title') || '';
      if (title) return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    // Approach 3: wizard state — window.WIZ.selectedMaterial.label
    if (window.WIZ && window.WIZ.selectedMaterial && window.WIZ.selectedMaterial.label) {
      return window.WIZ.selectedMaterial.label.toLowerCase().replace(/\s+/g, '-');
    }

    // Approach 4: wizard-swatch-item with .is-selected
    var wizSel = document.querySelector('.wizard-swatch-item.is-selected');
    if (wizSel) {
      var label = wizSel.getAttribute('data-label') || wizSel.getAttribute('title') || '';
      if (label) return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    // Approach 5: use the last variant's color option value
    if (this._lastVariant) {
      // Find which option is the color/vinyl option
      for (var i = 1; i <= 3; i++) {
        var optName = (this.productOptions[i - 1] || '').toLowerCase();
        if (optName === 'color' || optName === 'colour' || optName === 'vinyl' || optName === 'material') {
          var val = this._lastVariant['option' + i];
          if (val) return val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        }
      }
    }

    return null;
  };

  /**
   * Candidate material slugs to match against filenames: the LABEL slug from
   * _getMaterialSlug() plus (additive) the metaobject CODE slug when present.
   * Printed designs name images by CODE; solid/textured vinyls name them by
   * LABEL — a slide matches on EITHER. _getMaterialSlug stays single-valued so
   * the wipe-candidate / _lastMaterialSlug identity checks keep a stable value.
   */
  M2Gallery.prototype._getMaterialSlugs = function() {
    var slugs = [];
    var primary = this._getMaterialSlug();
    if (primary) slugs.push(primary);
    if (window.WIZ && window.WIZ.selectedMaterial && window.WIZ.selectedMaterial.code) {
      var codeSlug = String(window.WIZ.selectedMaterial.code)
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (codeSlug && slugs.indexOf(codeSlug) < 0) slugs.push(codeSlug);
    }
    return slugs;
  };

  /**
   * Get the currently selected kit value from the kit tile radios.
   */
  M2Gallery.prototype._getSelectedKit = function() {
    var checked = document.querySelector('.pf-kit-tile__radio:checked');
    if (checked) {
      var tile = checked.closest('.pf-kit-tile');
      return tile ? (tile.getAttribute('data-kit-value') || '') : '';
    }
    // Wizard kit selection
    var wizKit = document.querySelector('.wizard-kit-tile.is-selected');
    if (wizKit) return wizKit.getAttribute('data-kit-value') || '';
    if (window.WIZ && window.WIZ.selectedKit) return window.WIZ.selectedKit;
    return '';
  };

  /**
   * Count occurrences of a substring in a string, allowing overlapping
   * matches. Needed for slugs like `-pro-pro-` where the shared dash
   * means two `-pro-` occurrences overlap on one character — advancing by
   * sub.length would miss the second hit and break the "Pro product +
   * Pro kit" gallery filter (which requires 2 `-pro-` in the filename).
   */
  function countOccurrences(str, sub) {
    var count = 0, pos = 0;
    while ((pos = str.indexOf(sub, pos)) !== -1) { count++; pos += 1; }
    return count;
  }

  /**
   * Sort order value for a filename based on kit tier + position.
   * Lower = earlier in display order.
   */
  function slugSortOrder(filename) {
    var tier = 50; // default middle
    if (filename.indexOf('-ess-') >= 0) tier = 10;
    else if (filename.indexOf('-pro-') >= 0 || filename.indexOf('-prm-') >= 0) tier = 20;
    else if (filename.indexOf('-ult-') >= 0) tier = 30;

    var position = 0; // general views first
    if (filename.indexOf('-bottom') >= 0) position = 30;       // last
    else if (filename.indexOf('-interior') >= 0) position = 20; // next to last

    return tier * 100 + position;
  }

  /**
   * Which piece a filename shows: 'interior', 'bottom', or 'lid' (neither token).
   */
  function viewToken(filename) {
    if (filename.indexOf('-interior') >= 0) return 'interior';
    if (filename.indexOf('-bottom') >= 0) return 'bottom';
    return 'lid';
  }

  /**
   * Main filter method. Called on variant change and swatch change.
   */
  M2Gallery.prototype._filterBySlug = function() {
    var self = this;
    this._cancelWipe();
    var kitValue = this._getSelectedKit();
    var kitSlug = this._getKitSlug(kitValue);
    var materialSlug = this._getMaterialSlug();
    var materialSlugs = this._getMaterialSlugs();

    // Material-gated galleries (the wizard) hide the full image set behind a
    // blurred prompt until a material is chosen. Images stay in the DOM, so the
    // filter below still works the instant a material is picked.
    if (this.el.hasAttribute('data-require-material')) {
      var awaiting = !materialSlug;
      this.el.classList.toggle('m2-gallery--awaiting-material', awaiting);
      var prompt = this.el.querySelector('[data-m2-material-prompt]');
      if (prompt) prompt.setAttribute('aria-hidden', awaiting ? 'false' : 'true');
      // Kill any active hover-zoom so it can't stay stuck open behind the prompt.
      if (awaiting) {
        this.isZooming = false;
        if (this.zoomLens) this.zoomLens.hidden = true;
        if (this.zoomWindow) this.zoomWindow.hidden = true;
      }
    }


    var filenames = this.slides.map(function(s) {
      return (s.getAttribute('data-filename') || '').toLowerCase();
    });

    // The "Pro" KIT shares the '-pro-' token with the incidental '-pro-' a
    // "…Pro…" DEVICE slug injects into every filename (e.g. apple-ipad-PRO-…).
    // Only when that incidental token is present must the Pro kit require TWO
    // '-pro-' to isolate real Pro-kit images. Detect it from the FILENAMES, not
    // the product title: some Pro devices abbreviate the slug (framework-13p —
    // no '-pro-'), so the old title check wrongly demanded 2×, matched nothing,
    // and the Pro gallery fell back to material-only and showed every kit. An
    // Essentials image is never a Pro-kit image, so a '-pro-' inside one proves
    // the incidental device token; fall back to "every image carries '-pro-'".
    var essFiles = filenames.filter(function(f) { return f.indexOf('-ess-') >= 0; });
    var productHasPro = essFiles.length
      ? essFiles.some(function(f) { return f.indexOf('-pro-') >= 0; })
      : (filenames.length > 0 && filenames.every(function(f) { return f.indexOf('-pro-') >= 0; }));

    // Determine filter function with fallback cascade:
    // 1. kit + material match
    // 2. material only
    // 3. kit only
    // 4. show all
    var filterFn;

    function matchesKit(fname) {
      if (!kitSlug) return true;
      if (kitSlug === '-pro-' && productHasPro) {
        // Product name has "pro", so we need 2 occurrences of -pro- for kit match
        return countOccurrences(fname, '-pro-') >= 2;
      }
      return fname.indexOf(kitSlug) >= 0;
    }

    function matchesMaterial(fname) {
      if (!materialSlugs.length) return true;
      return materialSlugs.some(function(s) { return fname.indexOf(s) >= 0; });
    }

    if (kitSlug && materialSlug) {
      // Try kit + material
      if (filenames.some(function(f) { return matchesKit(f) && matchesMaterial(f); })) {
        filterFn = function(fname) { return matchesKit(fname) && matchesMaterial(fname); };
      }
      // Fallback: material only
      else if (filenames.some(function(f) { return matchesMaterial(f); })) {
        filterFn = function(fname) { return matchesMaterial(fname); };
      }
      // Fallback: kit only
      else if (filenames.some(function(f) { return matchesKit(f); })) {
        filterFn = function(fname) { return matchesKit(fname); };
      }
      // Final fallback: show all
      else {
        filterFn = function() { return true; };
      }
    } else if (materialSlug) {
      if (filenames.some(function(f) { return matchesMaterial(f); })) {
        filterFn = function(fname) { return matchesMaterial(fname); };
      } else {
        filterFn = function() { return true; };
      }
    } else if (kitSlug) {
      if (filenames.some(function(f) { return matchesKit(f); })) {
        filterFn = function(fname) { return matchesKit(fname); };
      } else {
        filterFn = function() { return true; };
      }
    } else {
      filterFn = function() { return true; };
    }

    // Material-change wipe: measure the outgoing slide now, before the filter hides it.
    var wipe = this._wipeCandidate(materialSlug, kitSlug);

    // Apply filter to slides and thumbs
    var firstVisibleId = null;
    this.slides.forEach(function(slide, idx) {
      var fname = (slide.getAttribute('data-filename') || '').toLowerCase();
      var show = filterFn(fname);
      slide.classList.toggle('is-filtered', !show);
      if (self.thumbs[idx]) {
        self.thumbs[idx].classList.toggle('is-filtered', !show);
      }
      if (show && !firstVisibleId) firstVisibleId = slide.getAttribute('data-media-id');
    });

    // Sort visible slides + thumbs by slug order
    this._sortBySlug();

    // Activate: stay on the piece the shopper was viewing (lid / interior / bottom) when the
    // new set has it — a material change used to snap back to the lid — else the first visible.
    var keepId = null;
    if (this.activeSlide) {
      var prevView = viewToken((this.activeSlide.getAttribute('data-filename') || '').toLowerCase());
      for (var k = 0; k < this.slides.length; k++) {
        var cand = this.slides[k];
        if (!cand.classList.contains('is-filtered') &&
            viewToken((cand.getAttribute('data-filename') || '').toLowerCase()) === prevView) {
          keepId = cand.getAttribute('data-media-id');
          break;
        }
      }
    }
    if (keepId || firstVisibleId) {
      // Same piece, new material, different slide → sweep the old image away.
      if (wipe && keepId && keepId !== wipe.id) this._beginWipe(wipe, keepId);
      else this._activate(keepId || firstVisibleId);
    }
    this._lastMaterialSlug = materialSlug;
    this._lastKitSlug = kitSlug;

    this._updateArrows();
  };

  /* =========================================================================
     Material-change wipe: a material pick on the same piece (lid / interior /
     bottom) sweeps the old image away along a feathered diagonal, revealing the
     new material beneath, with a soft glint riding the front. Anything else
     (kit change, thumbs, arrows, first pick, variant change) keeps the fade.
     ========================================================================= */

  /** Decide whether this _filterBySlug run wipes; snapshot the outgoing slide while it is still laid out. */
  M2Gallery.prototype._wipeCandidate = function(materialSlug, kitSlug) {
    var slide = this.activeSlide;
    var img = slide && slide.querySelector('.m2-gallery__image');
    if (!M2_WIPE.on || !this._lastMaterialSlug || !materialSlug ||
        materialSlug === this._lastMaterialSlug || kitSlug !== this._lastKitSlug ||
        !img || !img.complete || !img.naturalWidth || !img.classList.contains('is-loaded') ||
        slide.classList.contains('is-filtered') ||
        this.el.classList.contains('m2-gallery--awaiting-material') ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    var sr = slide.getBoundingClientRect(), ir = img.getBoundingClientRect();
    if (!sr.width || !ir.width) return null;
    return { id: slide.getAttribute('data-media-id'), el: img, main: this.main.getBoundingClientRect(), slide: sr, img: ir };
  };

  /** Activate the new slide under a clone of the old image, then sweep the clone away once the new one shows. */
  M2Gallery.prototype._beginWipe = function(w, newId) {
    var self = this;
    var newSlide = this.main.querySelector('.m2-gallery__slide[data-media-id="' + newId + '"]');
    var newImg = newSlide && newSlide.querySelector('.m2-gallery__image');
    if (!newImg) { this._activate(newId); return; }

    // The incoming image fades in hidden under the overlay anyway — snap it so the
    // front reveals a solid image, not one still mid-fade (restored on finish).
    newImg.style.transition = 'none';
    this._activate(newId);

    var m = w.main, sr = w.slide, ir = w.img, src = w.el.currentSrc || w.el.src;
    var pos = function(r) { return 'left:' + (r.left - m.left) + 'px;top:' + (r.top - m.top) + 'px;width:' + r.width + 'px;height:' + r.height + 'px;'; };
    var vars = '--m2-wipe-a:' + M2_WIPE.angle + 'deg;--m2-wipe-f:' + M2_WIPE.feather + '%;--m2-wipe-p:' + (-M2_WIPE.feather) + '%;';

    // Overlay covers the old slide's box; the clone sits exactly where the old image
    // rendered (explicit box + the same decoded src, so nothing can reflow).
    var box = document.createElement('div');
    box.className = 'm2-gallery__wipe';
    box.style.cssText = pos(sr) + vars;
    var clone = w.el.cloneNode(true);
    clone.className = 'm2-gallery__image is-loaded';
    clone.setAttribute('aria-hidden', 'true'); clone.draggable = false;
    clone.removeAttribute('srcset'); clone.removeAttribute('loading');
    clone.src = src;
    clone.style.cssText += ';position:absolute;margin:0;transition:none;left:' + (ir.left - sr.left) +
      'px;top:' + (ir.top - sr.top) + 'px;width:' + ir.width + 'px;height:' + ir.height + 'px';
    box.appendChild(clone);
    this.main.appendChild(box);

    // Glint shares the overlay's box so its band lines up with the front; the
    // old image masks it to the device silhouette (transparent PNG/WebP only).
    var glint = null;
    if (!this.el.classList.contains('is-jpg')) {
      glint = document.createElement('div');
      glint.className = 'm2-gallery__wipe-glint';
      glint.style.cssText = pos(sr) + vars + '--m2-wipe-g:' + M2_WIPE.glint + ';--m2-wipe-mask:url("' + src + '");' +
        '--m2-wipe-mask-pos:' + (ir.left - sr.left) + 'px ' + (ir.top - sr.top) + 'px;' +
        '--m2-wipe-mask-size:' + ir.width + 'px ' + ir.height + 'px';
      this.main.appendChild(glint);
    }

    this._wipe = { box: box, glint: glint, img: newImg, raf: 0, start: null, t0: performance.now() };
    this._wipe.raf = requestAnimationFrame(function(now) { self._runWipe(now); });
  };

  /**
   * rAF driver: waits for the new image to be revealed (is-loaded), then eases --m2-wipe-p
   * from -feather to 100+feather so the band fully enters and leaves. M2_WIPE.freeze (0..1)
   * holds the front at that progress for QA.
   */
  M2Gallery.prototype._runWipe = function(now) {
    var self = this, w = this._wipe;
    if (!w) return;
    var next = function() { w.raf = requestAnimationFrame(function(n) { self._runWipe(n); }); };
    if (w.start === null) {
      if (!w.img.classList.contains('is-loaded') || !w.img.naturalWidth) {
        if (now - w.t0 > 4000) this._cancelWipe(); else next();
        return;
      }
      w.start = now;
    }
    var f = M2_WIPE.feather, fz = M2_WIPE.freeze;
    var t = Math.min(1, (now - w.start) / M2_WIPE.dur);
    if (typeof fz === 'number') { t = fz; w.start = now - fz * M2_WIPE.dur; } // hold; clock re-based so it resumes from here
    var e = t * t * (3 - 2 * t);
    var p = (-f + e * (100 + 2 * f)) + '%';
    w.box.style.setProperty('--m2-wipe-p', p);
    if (w.glint) w.glint.style.setProperty('--m2-wipe-p', p);
    if (t >= 1 && typeof fz !== 'number') this._cancelWipe(); else next();
  };

  /** Stop a running wipe (finished, superseded or cancelled) and clean up. */
  M2Gallery.prototype._cancelWipe = function() {
    var w = this._wipe;
    if (!w) return;
    this._wipe = null;
    cancelAnimationFrame(w.raf);
    w.box.remove(); if (w.glint) w.glint.remove();
    w.img.style.transition = '';
  };

  /**
   * Re-order slides and thumbnails in the DOM by slug sort order.
   */
  M2Gallery.prototype._sortBySlug = function() {
    var main = this.main;
    var track = this.thumbTrack;

    // Get visible slides, sort them, re-append
    var visibleSlides = this.slides.filter(function(s) { return !s.classList.contains('is-filtered'); });
    var hiddenSlides = this.slides.filter(function(s) { return s.classList.contains('is-filtered'); });

    visibleSlides.sort(function(a, b) {
      var fa = (a.getAttribute('data-filename') || '').toLowerCase();
      var fb = (b.getAttribute('data-filename') || '').toLowerCase();
      return slugSortOrder(fa) - slugSortOrder(fb);
    });

    // Re-append in order: visible sorted first, then hidden
    visibleSlides.concat(hiddenSlides).forEach(function(s) { main.appendChild(s); });

    // Same for thumbs
    var visibleThumbs = this.thumbs.filter(function(t) { return !t.classList.contains('is-filtered'); });
    var hiddenThumbs = this.thumbs.filter(function(t) { return t.classList.contains('is-filtered'); });

    visibleThumbs.sort(function(a, b) {
      var fa = (a.getAttribute('data-filename') || '').toLowerCase();
      var fb = (b.getAttribute('data-filename') || '').toLowerCase();
      return slugSortOrder(fa) - slugSortOrder(fb);
    });

    visibleThumbs.concat(hiddenThumbs).forEach(function(t) { track.appendChild(t); });

    // Update internal arrays to match new DOM order
    this.slides = Array.from(main.querySelectorAll('.m2-gallery__slide'));
    this.thumbs = Array.from(track.querySelectorAll('.m2-gallery__thumb'));
  };

  /* =========================================================================
     Swatch/vinyl change listener — triggers slug re-filtering
     ========================================================================= */
  M2Gallery.prototype._bindSwatchChange = function() {
    var self = this;
    // Listen for clicks on swatch items / radio changes
    document.addEventListener('change', function(e) {
      if (e.target && (e.target.classList.contains('color-swatch__radio') ||
          e.target.closest('.pf-swatch-wrap'))) {
        // Small delay to let variant change process first
        setTimeout(function() { self._filterBySlug(); }, 100);
      }
    });

    // Also listen for kit tile changes
    document.addEventListener('change', function(e) {
      if (e.target && e.target.classList.contains('pf-kit-tile__radio')) {
        setTimeout(function() { self._filterBySlug(); }, 100);
      }
    });
  };

  /* =========================================================================
     Hover zoom on main image
     ========================================================================= */
  M2Gallery.prototype._bindZoom = function() {
    var self = this;
    var main = this.main;
    var lens = this.zoomLens;
    var zoomWin = this.zoomWindow;

    if (!window.matchMedia('(hover: hover) and (min-width: 641px)').matches) return;

    main.addEventListener('mouseenter', function() {
      if (self.el.classList.contains('m2-gallery--awaiting-material')) return;
      if (!self.activeSlide) return;
      var img = self.activeSlide.querySelector('.m2-gallery__image');
      if (!img) return;

      var zoomSrc = img.getAttribute('data-zoom-src') || img._m2originalSrc || img.src;

      if (!self.zoomImg || self.zoomImg._src !== zoomSrc) {
        self.zoomImg = new Image();
        self.zoomImg._src = zoomSrc;
        self.zoomImg.crossOrigin = 'anonymous';
        self.zoomImg.src = zoomSrc;
      }

      zoomWin.style.backgroundImage = 'url(' + zoomSrc + ')';
      lens.hidden = false;
      zoomWin.hidden = false;
      self.isZooming = true;
    });

    main.addEventListener('mousemove', function(e) {
      if (!self.isZooming || !self.activeSlide) return;

      var img = self.activeSlide.querySelector('.m2-gallery__image');
      if (!img) return;

      var mainRect = main.getBoundingClientRect();
      var imgRect = img.getBoundingClientRect();

      var x = e.clientX - mainRect.left;
      var y = e.clientY - mainRect.top;

      var lw = lens.offsetWidth / 2;
      var lh = lens.offsetHeight / 2;

      var lx = Math.max(0, Math.min(x - lw, mainRect.width - lens.offsetWidth));
      var ly = Math.max(0, Math.min(y - lh, mainRect.height - lens.offsetHeight));

      lens.style.left = lx + 'px';
      lens.style.top = ly + 'px';

      var zoomImg = self.zoomImg;
      if (!zoomImg || !zoomImg.naturalWidth) return;

      var zoomWinW = zoomWin.offsetWidth;
      var zoomWinH = zoomWin.offsetHeight;

      zoomWin.style.backgroundSize = zoomImg.naturalWidth + 'px ' + zoomImg.naturalHeight + 'px';

      var scaleX = zoomImg.naturalWidth / imgRect.width;
      var scaleY = zoomImg.naturalHeight / imgRect.height;

      var imgX = e.clientX - imgRect.left;
      var imgY = e.clientY - imgRect.top;

      var bgX = (imgX * scaleX) - (zoomWinW / 2);
      var bgY = (imgY * scaleY) - (zoomWinH / 2);

      zoomWin.style.backgroundPosition = (-bgX) + 'px ' + (-bgY) + 'px';
    });

    main.addEventListener('mouseleave', function() {
      lens.hidden = true;
      zoomWin.hidden = true;
      self.isZooming = false;
    });
  };

  /* =========================================================================
     Keyboard navigation
     ========================================================================= */
  document.addEventListener('keydown', function(e) {
    var gallery = document.querySelector('[data-m2-gallery]');
    if (!gallery || !gallery._m2gallery) return;

    if (e.key === 'ArrowLeft') {
      gallery._m2gallery._stepThumb(-1);
    } else if (e.key === 'ArrowRight') {
      gallery._m2gallery._stepThumb(1);
    }
  });

  /* =========================================================================
     Initialize
     ========================================================================= */
  document.addEventListener('DOMContentLoaded', function() {
    var galleryEl = document.querySelector('[data-m2-gallery]');
    if (galleryEl) {
      var gallery = new M2Gallery(galleryEl);
      galleryEl._m2gallery = gallery;
    }
  });

})();

/* ==========================================================================
   Random Pro Image — swap candidate images on page load
   ========================================================================== */
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('img.rpi-random[data-candidates]').forEach(function(img) {
    var candidates = img.getAttribute('data-candidates').split('||');
    if (candidates.length > 0) {
      img.src = candidates[Math.floor(Math.random() * candidates.length)];
    }
  });

  // Detect JPG images on product cards and set white bg
  document.querySelectorAll('.lpc__img img, .m2cf-card__img img').forEach(function(img) {
    function check() {
      var src = (img.currentSrc || img.src || '').toLowerCase();
      var container = img.closest('.lpc__img, .m2cf-card__img');
      if (container) {
        container.classList.toggle('is-jpg', /\.jpe?g/.test(src) || (!(/\.png|\.webp/.test(src)) && src !== ''));
      }
    }
    if (img.complete) check();
    else img.addEventListener('load', check, { once: true });
  });
});

/* ==========================================================================
   Product Tile Normalizer — consistent 15% margin around product in tiles
   Targets both .m2cf-card__photo (carousel + collection) and
   .product-item__primary-image (featured collections, related products).
   ========================================================================== */
(function() {
  'use strict';

  var MARGIN = 0.15;
  var FILL   = 1 - 2 * MARGIN;  // 0.70 — product occupies 70% of constraining axis
  var PROCESS_SIZE = 200;
  var TOLERANCE = 25;
  var SESSION_PREFIX = 'm2tn_';
  var SELECTORS = '.m2cf-card__photo, .product-item__primary-image';

  var memCache = {};

  // ---- pixel analysis: find bounding box of non-background content ----

  function pixelBounds(data, w, h) {
    var pts = [[0,0],[w-1,0],[0,h-1],[w-1,h-1],[w>>1,0],[0,h>>1],[w-1,h>>1],[w>>1,h-1]];
    var bgR = 0, bgG = 0, bgB = 0, n = 0;
    for (var i = 0; i < pts.length; i++) {
      var pi = (pts[i][1] * w + pts[i][0]) * 4;
      if (data[pi + 3] > 200) { bgR += data[pi]; bgG += data[pi+1]; bgB += data[pi+2]; n++; }
    }
    if (n > 0) { bgR /= n; bgG /= n; bgB /= n; }

    var x1 = w, y1 = h, x2 = 0, y2 = 0;
    for (var py = 0; py < h; py++) {
      for (var px = 0; px < w; px++) {
        var p = (py * w + px) * 4;
        if (data[p + 3] < 64) continue;
        if (Math.max(
          Math.abs(data[p]     - bgR),
          Math.abs(data[p + 1] - bgG),
          Math.abs(data[p + 2] - bgB)
        ) > TOLERANCE) {
          if (px < x1) x1 = px; if (px > x2) x2 = px;
          if (py < y1) y1 = py; if (py > y2) y2 = py;
        }
      }
    }
    if (x1 >= x2 || y1 >= y2) return null;
    return {
      x1: x1/w, y1: y1/h, x2: x2/w, y2: y2/h,
      bg: 'rgb('+Math.round(bgR)+','+Math.round(bgG)+','+Math.round(bgB)+')'
    };
  }

  function getBounds(src, callback) {
    var key = SESSION_PREFIX + src.split('?')[0];
    if (memCache[key]) { callback(memCache[key]); return; }
    var stored;
    try { stored = JSON.parse(sessionStorage.getItem(key)); } catch(e) {}
    if (stored) { memCache[key] = stored; callback(stored); return; }

    var corsUrl = src + (src.indexOf('?') > -1 ? '&' : '?') + '_m2cors=1';
    var ci = new Image();
    ci.crossOrigin = 'anonymous';
    ci.onload = function() {
      var W = ci.naturalWidth, H = ci.naturalHeight;
      if (!W || !H) { callback(null); return; }
      var sc = Math.min(1, PROCESS_SIZE / Math.max(W, H));
      var w = Math.round(W * sc), h = Math.round(H * sc);
      var cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      var ctx = cv.getContext('2d');
      ctx.drawImage(ci, 0, 0, w, h);
      var data;
      try { data = ctx.getImageData(0, 0, w, h).data; } catch(e) { callback(null); return; }
      var b = pixelBounds(data, w, h);
      if (b) { memCache[key] = b; try { sessionStorage.setItem(key, JSON.stringify(b)); } catch(e) {} }
      callback(b);
    };
    ci.onerror = function() { callback(null); };
    ci.src = corsUrl;
  }

  // ---- transform calculation ----
  // Accounts for CSS padding and object-fit on the image element so the transform
  // produces 15% margin relative to the CARD boundary, not just the image boundary.

  function computeTransform(img, bounds) {
    // Element's rendered border-box size
    var eW = img.offsetWidth, eH = img.offsetHeight;
    if (!eW || !eH) return null;

    var cs  = getComputedStyle(img);
    var pL  = parseFloat(cs.paddingLeft)   || 0;
    var pR  = parseFloat(cs.paddingRight)  || 0;
    var pT  = parseFloat(cs.paddingTop)    || 0;
    var pB  = parseFloat(cs.paddingBottom) || 0;
    var fit = cs.objectFit || 'fill';
    var nW  = img.naturalWidth, nH = img.naturalHeight;
    if (!nW || !nH) return null;

    // Where the image pixels actually render inside the border-box
    var cW = eW - pL - pR;
    var cH = eH - pT - pB;
    var rendW, rendH, offX, offY;

    if (fit === 'contain') {
      var fs = Math.min(cW / nW, cH / nH);
      rendW = nW * fs; rendH = nH * fs;
      offX  = pL + (cW - rendW) / 2;
      offY  = pT + (cH - rendH) / 2;
    } else {
      // fill / unset: image stretches to the content area
      rendW = cW; rendH = cH; offX = pL; offY = pT;
    }

    // Product bounding box in element-space pixels
    var pX1 = offX + bounds.x1 * rendW, pX2 = offX + bounds.x2 * rendW;
    var pY1 = offY + bounds.y1 * rendH, pY2 = offY + bounds.y2 * rendH;
    var pW  = pX2 - pX1, pH = pY2 - pY1;
    if (pW <= 0 || pH <= 0) return null;

    // Scale so constraining axis has MARGIN on each side; translate to centre
    var s  = Math.min(FILL * eW / pW, FILL * eH / pH);
    var tx = (0.5 - (pX1 + pX2) / (2 * eW)) * 100;
    var ty = (0.5 - (pY1 + pY2) / (2 * eH)) * 100;

    return { s: s, tx: tx, ty: ty };
  }

  // ---- apply ----

  function applyTransform(img, bounds) {
    var t = computeTransform(img, bounds);
    if (!t) return;
    var s = t.s, tx = t.tx, ty = t.ty;

    var normT  = 'scale('+s.toFixed(4)+') translate('+tx.toFixed(2)+'%,'+ty.toFixed(2)+'%)';
    var hoverT = 'scale('+(s*1.15).toFixed(4)+') translate('+tx.toFixed(2)+'%,'+ty.toFixed(2)+'%)';

    if (img.classList.contains('m2cf-card__photo')) {
      // Use CSS custom props so the existing hover rule can compound with the
      // normalization scale instead of being silently overridden by inline style.
      img.style.setProperty('--m2-norm-t', normT);
      img.style.setProperty('--m2-hover-t', hoverT);
      img.classList.add('m2-norm');
    } else {
      // product-item__primary-image: no competing hover CSS, inline style is fine
      var container = img.parentElement;
      if (container) {
        container.style.overflow = 'hidden';
        if (s < 1) container.style.background = bounds.bg;
      }
      img.style.transformOrigin = 'center center';
      img.style.transform = normT;
    }
  }

  // ---- per-image entry point ----

  function processImg(img) {
    if (img._m2normalized) return;
    var src = img.currentSrc || img.src || '';
    if (!src) return;
    img._m2normalized = true;
    getBounds(src, function(b) { if (b) applyTransform(img, b); });
  }

  function initImg(img) {
    if (img._m2normalized) return;
    if (img.complete && img.naturalWidth) { processImg(img); }
    else { img.addEventListener('load', function() { processImg(img); }, { once: true }); }
  }

  // ---- init ----

  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll(SELECTORS).forEach(initImg);

    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.querySelectorAll) {
            node.querySelectorAll(SELECTORS).forEach(initImg);
          }
          if (node.classList &&
              (node.classList.contains('m2cf-card__photo') ||
               node.classList.contains('product-item__primary-image'))) {
            initImg(node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  });

})();
